// Matches a product's admin-typed printMethod free-text value against the
// set of real-world labels vendors/admins have used for embossing/debossing
// methods, so the embossing-preview module
// (services/embossingPreviewService.ts) can recognize a design that should
// get the real relief-rendered preview. Mirrors embroideryPreview.ts's own
// EMBROIDERY_METHOD_KEYWORDS matching convention exactly (see that file's
// comment for the full rationale on why a substring match, not an enum).
//
// "Blinddruk" (Dutch for blind embossing) and "Debossing" are real vendor
// print-method strings already seen in seeded product data (see
// IMPLEMENTATION-STATUS.md's changelog references to
// manualCheckTrue_PX107168_voorzijde_Blinddruk.png and SKU AR1804's
// "Debossing" method) — both covered here since debossing (pressed in) and
// embossing (raised) share the same relief-rendering technique, just an
// inverted bevel direction (see isDebossPrintMethod below).
const EMBOSSING_METHOD_KEYWORDS = ['emboss', 'debossing', 'blinddruk', 'blind embossing', 'reliëf', 'relief'];

export function isEmbossingPrintMethod(printMethod: string | null | undefined): boolean {
  if (!printMethod) return false;
  const lower = printMethod.toLowerCase();
  return EMBOSSING_METHOD_KEYWORDS.some(kw => lower.includes(kw));
}

// Deboss (pressed IN) vs. emboss (raised) — same relief-lighting technique,
// opposite bevel direction (see emboss_relief_lighting.py's `direction` arg).
// "Deboss"/"Blinddruk" are the real-world signals for the pressed-in look;
// anything else matching isEmbossingPrintMethod() but not this defaults to
// raised emboss.
const DEBOSS_METHOD_KEYWORDS = ['deboss', 'blinddruk'];

export function isDebossPrintMethod(printMethod: string | null | undefined): boolean {
  if (!printMethod) return false;
  const lower = printMethod.toLowerCase();
  return isEmbossingPrintMethod(printMethod) && DEBOSS_METHOD_KEYWORDS.some(kw => lower.includes(kw));
}

export const EMBOSSING_PREVIEW_DESIGN_IMAGE_TYPE = 'embossing_preview';

// Design.embossingPreviewStatus int convention — mirrors
// EmbroideryPreviewStatus (see schema.prisma's own comment on the column).
export const EmbossingPreviewStatus = {
  NONE:       0,
  QUEUED:     1,
  PROCESSING: 2,
  DONE:       3,
  FAILED:     4,
} as const;
export type EmbossingPreviewStatusValue = typeof EmbossingPreviewStatus[keyof typeof EmbossingPreviewStatus];
